import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  TablePagination,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import WorkflowTemplateServiceController from "../../Controllers/WorkflowTemplateservice";
import ConfirmPopup from '../Common Components/Confirm_Popup';
import CustomToaster, { notify } from "../Common Components/Custom_Toaster";
import NoteIcon from "@mui/icons-material/Note"; // import at top
import "./Workflow InstanceList.css";
import { FaFile } from "react-icons/fa";
 
/* ===================== STYLES ===================== */
const StyledBodyCell = styled(TableCell)(() => ({
  fontSize: "14px",
  fontWeight: 600,
  color: "#121B45",
  borderBottom: "1px solid #cfd4e9",
  fontFamily: "Montserrat, sans-serif",
  paddingTop: "6px",      // reduce top padding
  paddingBottom: "6px",   // reduce bottom padding
}));

const StyledHeaderCell = styled(TableCell)(() => ({
  fontSize: "14px",
  fontWeight: 500,
  color: "#757EA5",
  borderBottom: "1px solid #cfd4e9",
  backgroundColor: "#FFFFFF",
  fontFamily: "Montserrat, sans-serif",
  paddingTop: "16px",      // reduce top padding
  paddingBottom: "6px",   // reduce bottom padding
}));


const StyledRow = styled(TableRow)(() => ({
  height: "35px",
  "&:nth-of-type(odd)": {
    backgroundColor: "#F3FBFF",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "#FFFFFF",
  },
}));

/* ===================== COMPONENT ===================== */
const WorkflowInstanceList = () => {
  const controller = new WorkflowTemplateServiceController();
  const navigate = useNavigate();

  /* ===================== STATE ===================== */
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmPopupData, setConfirmPopupData] = useState({
    dataId: null,
    template: '',
    popupType: '',
    name: ''
  });
  const [showViewNoteModal, setShowViewNoteModal] = useState(false);

  /* ===================== API ===================== */
  const workflowGetAllInstance = async () => {
    try {
      const res = await controller.getworkflowGetAllInstance();
      setRows(res || []);
    } catch (err) {
      console.error(err);
      setRows([]);
    }
  };

  useEffect(() => {
    workflowGetAllInstance();
  }, []);

  /* ===================== SEARCH FILTER ===================== */
  const filteredRows = rows.filter((row) =>
    Object.values(row).some((val) =>
      val?.toString().toLowerCase().includes(search.toLowerCase())
    )
  );

  /* ===================== SORT ===================== */
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedRows = [...filteredRows].sort((a, b) => {
    if (!orderBy) return 0;
    const aVal = a[orderBy]?.toString() || "";
    const bVal = b[orderBy]?.toString() || "";
    return order === "asc"
      ? aVal.localeCompare(bVal)
      : bVal.localeCompare(aVal);
  });

  /* ===================== PAGINATION ===================== */
  const paginatedRows = sortedRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* ===================== MENU HANDLERS ===================== */
  const openMenu = (e, row) => {
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleEdit = () => {
    navigate("/", { state: { mode: "edit", workflowData: selectedRow } });
    closeMenu();
  };

  const handleClone = () => {
    alert("Clone clicked");
    closeMenu();
  };

  const togglePopup = (type = "", row = null) => {
    if (row) {
      setConfirmPopupData(
        {
          dataId: row.id,
          template: "",
          popupType: type,
          name: row.instanceName || row.workflowName,
        }
      )
      //setName(name);

    }
    setIsPopupOpen((prev) => !prev);
  };

  const handleDeleteClick = () => {
    togglePopup("delete", selectedRow);
    closeMenu();
  };

  const handleCloneClick = () => {
    togglePopup("clone", selectedRow);
    closeMenu();
  };

  /* ===================== POPUP HANDLERS ===================== */
  const handleDeleteTemplate = async () => {
    try {
      setIsLoading(true);
      await controller.postworkflowDeleteWorkflowInstance(confirmPopupData.dataId);
      await workflowGetAllInstance();
      togglePopup(); // close popup    
      await notify(
        `${confirmPopupData.name} has been successfully deleted`,
        true
      )
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloneTemplate = async () => {
    try {
      setIsLoading(true);
      await controller.postworkflowCloneWorkflowInstance(confirmPopupData.dataId);
      await workflowGetAllInstance();
      togglePopup();
      await notify(
        `${confirmPopupData.name} has been successfully cloned`,
        true
      )
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewNoteClick = (row) => {
    setSelectedRow(row);
    setShowViewNoteModal(true);
  };

  /* ===================== RENDER ===================== */
  return (
    <>
      {/* ===================== SEARCH BAR ===================== */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          margin: "15px",
        }}
      >
        <TextField
          size="small"
          placeholder="Search..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{
            width: 400,
            height: 50,
            "& .MuiOutlinedInput-root": {
              fontFamily: "Montserrat, sans-serif",
              fontSize: "14px",
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon sx={{ color: "#757EA5" }} />
              </InputAdornment>
            ),
          }}
        />
      </div>

      {/* ===================== TABLE ===================== */}
      <TableContainer
        component={Paper}
        sx={{
          margin: "0 16px",
          border: "1px solid #cfd4e9",
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <StyledHeaderCell>S.No</StyledHeaderCell> {/* New S.No column */}
              {[
                { label: "Callout Name", key: "calloutName" },
                { label: "Instance Name", key: "instanceName" },
                { label: "Workflow Name", key: "workflowName" },
                { label: "Job Title", key: "title" },
                { label: "Client", key: "client" },
                { label: "Department", key: "department" },
                { label: "Created By", key: "createdBy" },
                { label: "Created Date", key: "strCreatedDate" },
              ].map((col) => (
                <StyledHeaderCell key={col.key}>
                  <TableSortLabel
                    active={orderBy === col.key}
                    direction={orderBy === col.key ? order : "asc"}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                  </TableSortLabel>
                </StyledHeaderCell>
              ))}
              <StyledHeaderCell>Notes</StyledHeaderCell>
              <StyledHeaderCell>Action</StyledHeaderCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedRows.map((row, index) => (
              <StyledRow key={row.id}>
                <StyledBodyCell>
                  {page * rowsPerPage + index + 1}
                </StyledBodyCell>
                <StyledBodyCell>{row.instanceName}</StyledBodyCell>
                <StyledBodyCell
                  sx={{
                    color: "#02ACF6",
                    cursor: "pointer",
                    textTransform: "uppercase",
                  }}
                >
                  {row.calloutName}
                </StyledBodyCell>
                <StyledBodyCell
                  sx={{
                    color: "#02ACF6",
                    cursor: "pointer",
                    textTransform: "uppercase",
                  }}
                >
                  {row.workflowName}
                </StyledBodyCell>

                <StyledBodyCell>{row.title}</StyledBodyCell>
                <StyledBodyCell>{row.client}</StyledBodyCell>
                <StyledBodyCell>{row.department}</StyledBodyCell>
                <StyledBodyCell>{row.createdBy}</StyledBodyCell>
                <StyledBodyCell>{row.strCreatedDate}</StyledBodyCell>
                <StyledBodyCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => handleViewNoteClick(row)}
                    disabled={!row.noteList || row.noteList.length === 0} // optional: disable if no notes
                  >
                    {/* <NoteIcon  size={22} sx={{ color: "#f59210ff" }} /> */}
                    <FaFile size={22} color="#f5cb10ff" />
                  </IconButton>
                </StyledBodyCell>
                <StyledBodyCell align="center">
                  <IconButton size="small" onClick={(e) => openMenu(e, row)}>
                    <MoreVertIcon sx={{ color: "#757EA5" }} />
                  </IconButton>
                </StyledBodyCell>
              </StyledRow>
            ))}
          </TableBody>
        </Table>

        {/* ===================== MENU ===================== */}
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
          <Tooltip
            title={
              selectedRow?.jobStatus === "Closed"
                ? "The job is closed and cannot be edited."
                : ""
            }
            arrow
          > <span>
            <MenuItem onClick={handleEdit} disabled={selectedRow?.jobStatus === "Closed"}>Edit</MenuItem> </span>
          </Tooltip>
          <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>
          {/* <MenuItem onClick={handleCloneClick}>Clone</MenuItem> */}
        </Menu>

        {/* ===================== PAGINATION ===================== */}
        <TablePagination
          component="div"
          count={filteredRows.length}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </TableContainer>

      {/* ===================== CONFIRM POPUP ===================== */}
      <ConfirmPopup
        openConfirmationPopup={isPopupOpen}
        onClose={togglePopup}
        handleDeleteTemplate={handleDeleteTemplate}
        handleCloneTemplate={handleCloneTemplate}
        isLoading={isLoading}
        popupData={confirmPopupData}
      />
      <CustomToaster />
      {showViewNoteModal && selectedRow && (
        <div className="modal-overlay">
          <div className="view-notes-modal">
            {/* Header */}
            <div className="view-notes-header">
              <h3>View Notes</h3>
              <button
                className="close-btn"
                onClick={() => {
                  setShowViewNoteModal(false);
                  setSelectedRow(null);
                }}
              >
                ✕
              </button>
            </div>

            {/* Table */}
            <div className="view-notes-table-wrapper">
              <table className="view-notes-table">
                <thead>
                  <tr>
                    <th>Note</th>
                    <th>Created By</th>
                    <th>Created Date &amp; Time</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedRow.noteList?.length ? (
                    selectedRow.noteList.map((note, index) => (
                      <tr key={index}>
                        <td>{note.notes}</td>
                        <td>{note.createdBy}</td>
                        <td>{note.createdDate}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="no-note">
                        No notes available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}


    </>
  );
};

export default WorkflowInstanceList;
